// src/services/timelineService.js
// Camada de acesso à Timeline do Termometeiro (Firestore)

import { db } from "../firebase";
import {
  doc, getDoc, setDoc, updateDoc, collection, addDoc, serverTimestamp,
  query, where, orderBy, limit, getDocs, increment, runTransaction
} from "firebase/firestore";

// =============================
// Constantes / Coleções
// =============================
const CFG_COLL    = "timelineConfig";
const CFG_DOC     = "public";         // config pública (postingMode, blockedUIDs)
const FEED_COLL   = "timelineChunks";
const INDEX_COLL  = "timelineIndex";
const POSTS_COLL  = "timelinePosts";

const CHUNK_TARGET_SIZE = 50;
const TEXT_HARD_CAP     = 1500;       // só para o texto do post
const COOLDOWN_SECONDS  = 900;        // 15 minutos entre posts
const RULES_COLL        = "timelineRules";

// =============================
// Utils
// =============================

function sanitizePlainText(s = "") {
  return s.replace(/https?:\/\/\S+/gi, "").replace(/www\.\S+/gi, "").trim();
}
function newId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
// Detecta spoiler inline do tipo [spoiler]...[/spoiler]
function textHasInlineSpoiler(t = "") {
  return /\[spoiler\][\s\S]*?\[\/spoiler\]/i.test(String(t || ""));
}

// Substitui qualquer undefined (e NaN) por null, recursivamente.
function deepStripUndefined(v) {
  if (v === undefined) return null;
  if (Array.isArray(v)) return v.map(deepStripUndefined);
  if (v && typeof v === "object") {
    const out = {};
    for (const [k, val] of Object.entries(v)) {
      const cleaned = deepStripUndefined(val);
      if (cleaned !== undefined) out[k] = cleaned;
    }
    return out;
  }
  if (typeof v === "number" && Number.isNaN(v)) return null;
  return v;
}

// Normaliza attach para entrar no array "posts" do chunk sem undefined
const PLACEHOLDER_IMG = "https://i.imgur.com/6MbQoCt.png";
function sanitizeAttachForChunk(attach) {
  if (!attach || !attach.modo || !attach.categoria || !Array.isArray(attach.itens)) return null;
  const itensSan = attach.itens
    .filter(Boolean)
    .map((raw, idx) => {
      const it = raw || {};
      let acertou = null;
      if (it.acertou === true || it.acertou === "true" || it.acertou === 1) acertou = true;
      else if (it.acertou === false || it.acertou === "false" || it.acertou === 0) acertou = false;

      const ordemNum =
        typeof it.ordem === "number"
          ? it.ordem
          : it.ordem != null && !Number.isNaN(Number(it.ordem))
          ? Number(it.ordem)
          : idx + 1;

      return {
        titulo: (it.titulo || "").trim(),
        filme: it.filme ?? null,
        pais: it.pais ?? null,
        imagem: it.imagem || PLACEHOLDER_IMG,
        ordem: ordemNum,
        acertou, // true | false | null
      };
    });

  return {
    modo: String(attach.modo),
    categoria: String(attach.categoria),
    itens: itensSan,
  };
}

/** Lista pública das regras (ordenadas por 'order' asc) */
export async function listCommunityRules() {
  const q = query(collection(db, RULES_COLL), orderBy("order", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map((d, i) => {
    const data = d.data() || {};
    return {
      id: d.id,
      order: typeof data.order === "number" ? data.order : i + 1,
      title: data.title || "",
      text: data.text || "",
      updatedAt: data.updatedAt || null,
    };
  });
}

/** Cria/atualiza regra (somente admin pelas rules) */
export async function upsertCommunityRule({ id = null, order = 1, title = "", text = "" }) {
  const payload = {
    order: Number(order) || 1,
    title: String(title || "").trim(),
    text: String(text || "").trim(),
    updatedAt: serverTimestamp(),
  };
  if (id) {
    const ref = doc(db, RULES_COLL, id);
    await setDoc(ref, payload, { merge: true });
    return id;
  } else {
    const ref = await addDoc(collection(db, RULES_COLL), payload);
    return ref.id;
  }
}

/** Apaga uma regra (somente admin pelas rules) */
export async function deleteCommunityRule(id) {
  if (!id) return;
  await (await import("firebase/firestore")).deleteDoc(doc(db, RULES_COLL, id));
}

/** Metas reais de vários posts (likes/replies/text/etc.) */
export async function getPostMetaMany(postIds = []) {
  const tasks = postIds.map((id) => getDoc(doc(db, POSTS_COLL, id)));
  const snaps = await Promise.all(tasks);
  return snaps
    .filter((s) => s.exists())
    .map((s) => {
      const d = s.data() || {};
      return {
        postId: d.postId || s.id,
        likesCount: d.likesCount || 0,
        repliesCount: d.repliesCount || 0,
        text: typeof d.text === "string" ? d.text : null,
        editedAt: d.editedAt || null,
        hasSpoiler: !!d.hasSpoiler,
        deleted: !!d.deleted,                 // <<< importante pra UI ocultar
        status: d.status || "published",      // idem
      };
    });
}

/** Checa se o usuário curtiu (subcoleção por usuário) */
export async function hasLiked({ postId, userUid }) {
  if (!postId || !userUid) return false;
  const likeRef = doc(db, POSTS_COLL, postId, "likes", userUid);
  const snap = await getDoc(likeRef);
  return snap.exists();
}

// =============================
// Config / Admin
// =============================
export async function getTimelineConfig() {
  const ref = doc(db, CFG_COLL, CFG_DOC);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    return { postingMode: "auto", blockedUIDs: [] };
  }
  const data = snap.data() || {};
  return {
    postingMode: data.postingMode === "moderated" ? "moderated" : "auto",
    blockedUIDs: Array.isArray(data.blockedUIDs) ? data.blockedUIDs : [],
  };
}

export async function setPostingMode(mode) {
  if (!["auto", "moderated"].includes(mode)) throw new Error("Modo inválido");
  const ref = doc(db, CFG_COLL, CFG_DOC);
  await updateDoc(ref, { postingMode: mode, updatedAt: serverTimestamp() });
}

export async function blockUser(uid) {
  const ref = doc(db, CFG_COLL, CFG_DOC);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const data = snap.exists() ? snap.data() : {};
    const setBloq = new Set(Array.isArray(data.blockedUIDs) ? data.blockedUIDs : []);
    setBloq.add(uid);
    tx.set(
      ref,
      {
        postingMode: data.postingMode || "auto",
        blockedUIDs: Array.from(setBloq),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  });
}

export async function unblockUser(uid) {
  const ref = doc(db, CFG_COLL, CFG_DOC);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const data = snap.exists() ? snap.data() : {};
    const setBloq = new Set(Array.isArray(data.blockedUIDs) ? data.blockedUIDs : []);
    setBloq.delete(uid);
    tx.set(
      ref,
      {
        postingMode: data.postingMode || "auto",
        blockedUIDs: Array.from(setBloq),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  });
}

export async function isUserBlocked(uid) {
  const cfg = await getTimelineConfig();
  return Array.isArray(cfg.blockedUIDs) && cfg.blockedUIDs.includes(uid);
}

// =============================
// Chunks (feed paginado)
// =============================
export async function getCurrentChunk() {
  const qCur = query(collection(db, FEED_COLL), where("isCurrent", "==", true), limit(1));
  const snap = await getDocs(qCur);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

export async function getChunkByIndex(index) {
  const qIdx = query(collection(db, FEED_COLL), where("index", "==", index), limit(1));
  const snap = await getDocs(qIdx);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

export async function getPrevChunk(currentIndex) {
  const target = currentIndex - 1;
  if (target < 0) return null;
  return getChunkByIndex(target);
}

// =============================
// Posts
// =============================
export async function createPost({ author, text, attach, postingMode = "auto", hasSpoiler = false }) {
  if (!author?.uid) throw new Error("Autor inválido");
  if (!text && !attach) throw new Error("É necessário texto ou anexo de aposta");

  // bloqueio
  if (await isUserBlocked(author.uid)) {
    throw new Error("Você está temporariamente impedido de comentar na timeline.");
  }

  // 🔒 hard-cap SOMENTE no texto (não corta candidatos)
  const cleanText = sanitizePlainText((text || "").slice(0, TEXT_HARD_CAP));
  const safeAttach = sanitizeAttachForChunk(attach);

  // também detecta spoiler inline
  const computedHasSpoiler = !!hasSpoiler || textHasInlineSpoiler(cleanText);

  const postId = newId();
  const status = postingMode === "moderated" ? "pending" : "published";

  // Versão “segura” para o array do chunk
  const postLiteForChunk = deepStripUndefined({
    postId,
    authorUid: author.uid,
    authorName: author.name || "Usuário",
    authorAvatar: author.avatar || "",
    especialista: !!author.especialista,
    text: cleanText,
    attach: safeAttach,
    hasSpoiler: computedHasSpoiler,
    likesCount: 0,
    repliesCount: 0,
    status,                      // published|pending|rejected|deleted
    createdAtMs: Date.now(),
    editedAtMs: null,
  });

  // 1) Garante o chunk "current"
  let current = await getCurrentChunk();
  if (!current) {
    const ref = await addDoc(collection(db, FEED_COLL), {
      index: 0,
      isCurrent: true,
      createdAt: serverTimestamp(),
      posts: [],
    });
    current = { id: ref.id, index: 0, isCurrent: true, posts: [] };
  }

  // 2) Transação: cooldown + insert/rotate
  const result = await runTransaction(db, async (tx) => {
    const userRateRef = doc(collection(db, "usuarios", author.uid, "timelineMeta"), "rate");
    const userRateSnap = await tx.get(userRateRef);

    const nowMs = Date.now();
    if (userRateSnap.exists()) {
      const last = userRateSnap.data()?.lastPostAt;
      const lastMs = last?.toMillis ? last.toMillis() : (typeof last === "number" ? last : 0);
      const diffSec = Math.floor((nowMs - lastMs) / 1000);
      if (diffSec < COOLDOWN_SECONDS) {
        throw new Error(`Aguarde ${COOLDOWN_SECONDS - diffSec}s para postar novamente.`);
      }
    }

    const currentRef = doc(db, FEED_COLL, current.id);
    const curSnap = await tx.get(currentRef);
    if (!curSnap.exists()) throw new Error("Chunk atual não encontrado. Tente novamente.");

    const curData = curSnap.data() || { posts: [], index: 0, isCurrent: true };
    const curPosts = Array.isArray(curData.posts) ? curData.posts : [];
    const mustRotate = !curData.isCurrent || curPosts.length >= CHUNK_TARGET_SIZE;

    let targetChunkId = currentRef.id;

    if (mustRotate) {
      tx.update(currentRef, { isCurrent: false });

      const newRef = doc(collection(db, FEED_COLL));
      const newChunk = {
        index: (curData.index ?? 0) + 1,
        isCurrent: true,
        createdAt: serverTimestamp(),
        posts: [postLiteForChunk],
      };
      tx.set(newRef, newChunk);
      targetChunkId = newRef.id;
    } else {
      const newPosts = [...curPosts, postLiteForChunk].map(deepStripUndefined);
      tx.update(currentRef, { posts: newPosts });
    }

    // índice
    const idxRef = doc(collection(db, INDEX_COLL));
    tx.set(idxRef, {
      postId,
      chunkId: targetChunkId,
      createdAt: serverTimestamp(),
    });

    // meta
    const metaRef = doc(db, POSTS_COLL, postId);
    tx.set(metaRef, {
      postId,
      chunkId: targetChunkId,
      authorUid: author.uid,
      status,
      likesCount: 0,
      repliesCount: 0,
      hasSpoiler: computedHasSpoiler,
      deleted: false,
      createdAt: serverTimestamp(),
      editedAt: null,
    });

    // cooldown
    tx.set(userRateRef, { lastPostAt: serverTimestamp() }, { merge: true });

    return { postId, chunkId: targetChunkId, status };
  });

  return result;
}

export async function editPost({ postId, newText, hasSpoiler }) {
  const clean = sanitizePlainText(newText || "");
  const computedHasSpoiler =
    typeof hasSpoiler === "boolean" ? hasSpoiler : textHasInlineSpoiler(clean);
  const ref = doc(db, POSTS_COLL, postId);
  await updateDoc(ref, {
    text: clean,
    editedAt: serverTimestamp(),
    hasSpoiler: computedHasSpoiler,
  });
}

/** Deleção “completa” (toca no chunk) — normalmente só admin pode */
export async function softDeletePost(postId) {
  const q = query(collection(db, INDEX_COLL), where("postId", "==", postId), limit(1));
  const idxSnap = await getDocs(q);
  if (idxSnap.empty) return;

  const { chunkId } = idxSnap.docs[0].data();

  await runTransaction(db, async (tx) => {
    const chunkRef = doc(db, FEED_COLL, chunkId);
    const chunkSnap = await tx.get(chunkRef);
    if (!chunkSnap.exists()) return;

    const data = chunkSnap.data();
    const posts = Array.isArray(data.posts) ? data.posts : [];
    const i = posts.findIndex((p) => p.postId === postId);
    if (i >= 0) {
      posts[i] = deepStripUndefined({ ...posts[i], status: "deleted" });
      tx.update(chunkRef, { posts });
    }
    tx.update(doc(db, POSTS_COLL, postId), { deleted: true, status: "deleted" });
  });
}

/** Deleção pelo próprio autor: só marca no META (não toca no chunk) */
export async function softDeletePostByAuthor(postId) {
  if (!postId) throw new Error("postId ausente");
  const ref = doc(db, POSTS_COLL, postId);
  await updateDoc(ref, { deleted: true, status: "deleted" });
}

export async function hardDeletePost(postId) {
  const q = query(collection(db, INDEX_COLL), where("postId", "==", postId), limit(1));
  const idxSnap = await getDocs(q);
  if (idxSnap.empty) return;

  const idxDoc = idxSnap.docs[0];
  const { chunkId } = idxDoc.data();

  await runTransaction(db, async (tx) => {
    const chunkRef = doc(db, FEED_COLL, chunkId);
    const chunkSnap = await tx.get(chunkRef);
    if (chunkSnap.exists()) {
      const data = chunkSnap.data();
      const posts = (Array.isArray(data.posts) ? data.posts : []).filter(
        (p) => p.postId !== postId
      );
      tx.update(chunkRef, { posts });
    }
    tx.delete(doc(db, INDEX_COLL, idxDoc.id));
    tx.delete(doc(db, POSTS_COLL, postId));
    // (apagar subcoleção replies: fazer via util/admin fora da transaction)
  });
}

export async function setPostStatus(postId, status) {
  if (!["published", "pending", "rejected", "deleted"].includes(status)) {
    throw new Error("Status inválido");
  }
  const qIdx = query(collection(db, INDEX_COLL), where("postId", "==", postId), limit(1));
  const idxSnap = await getDocs(qIdx);
  if (idxSnap.empty) throw new Error("Post não encontrado (índice)");
  const { chunkId } = idxSnap.docs[0].data();

  await runTransaction(db, async (tx) => {
    const chunkRef = doc(db, FEED_COLL, chunkId);
    const chunkSnap = await tx.get(chunkRef);
    if (!chunkSnap.exists()) throw new Error("Chunk do post não existe");

    const data = chunkSnap.data();
    const posts = Array.isArray(data.posts) ? data.posts : [];
    const i = posts.findIndex((p) => p.postId === postId);
    if (i < 0) throw new Error("Post não encontrado no chunk");

    // atualiza status no chunk
    posts[i] = deepStripUndefined({ ...posts[i], status });
    tx.update(chunkRef, { posts });

    // atualiza meta; se for publish, garante deleted:false
    const metaRef = doc(db, POSTS_COLL, postId);
    const metaUpdate =
      status === "published" ? { status, deleted: false } : { status };
    tx.update(metaRef, metaUpdate);
  });
}

// =============================
// Likes (toggle por usuário)
// =============================
export async function toggleLike({ postId, userUid, like }) {
  if (!postId || !userUid) throw new Error("Parâmetros inválidos");

  const postRef = doc(db, POSTS_COLL, postId);
  const likeRef = doc(db, POSTS_COLL, postId, "likes", userUid);

  await runTransaction(db, async (tx) => {
    const [postSnap, likeSnap] = await Promise.all([tx.get(postRef), tx.get(likeRef)]);
    if (!postSnap.exists()) throw new Error("Post não existe");

    const already = likeSnap.exists();

    // nada muda → no-op
    if (like && already) return;
    if (!like && !already) return;

    const cur = postSnap.data() || {};
    let nextCount = cur.likesCount || 0;

    if (like && !already) {
      nextCount = nextCount + 1;
      tx.set(likeRef, { uid: userUid, createdAt: serverTimestamp() });
    } else if (!like && already) {
      nextCount = Math.max(0, nextCount - 1);
      tx.delete(likeRef);
    }

    tx.update(postRef, { likesCount: nextCount });
  });
}

// =============================
// Replies
// =============================
export async function addReply({ postId, author, text, postingMode = "auto", hasSpoiler = false }) {
  if (!author?.uid) throw new Error("Autor inválido");
  if (await isUserBlocked(author.uid)) {
    throw new Error("Você está temporariamente impedido de comentar na timeline.");
  }
  const clean = sanitizePlainText(text || "");
  if (!clean) throw new Error("Reply vazio");

  const status = postingMode === "moderated" ? "pending" : "published";
  const computedHasSpoiler = !!hasSpoiler || textHasInlineSpoiler(clean);

  const replyRef = await addDoc(collection(db, POSTS_COLL, postId, "replies"), {
    replyId: newId(),
    postId,
    authorUid: author.uid,
    authorName: author.name || "Usuário",
    authorAvatar: author.avatar || "",
    especialista: !!author.especialista,
    text: clean,
    hasSpoiler: computedHasSpoiler,
    status,
    deleted: false,
    createdAt: serverTimestamp(),
    editedAt: null,
  });

  await updateDoc(doc(db, POSTS_COLL, postId), { repliesCount: increment(1) });
  return replyRef.id;
}

export async function listReplies({ postId, pageSize = 30 }) {
  const q = query(
    collection(db, POSTS_COLL, postId, "replies"),
    where("deleted", "==", false),
    where("status", "==", "published"),
    orderBy("createdAt", "asc"),
    limit(pageSize)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function editReply({ postId, replyId, newText, hasSpoiler }) {
  const clean = sanitizePlainText(newText || "");
  const computedHasSpoiler =
    typeof hasSpoiler === "boolean" ? hasSpoiler : textHasInlineSpoiler(clean);
  const ref = doc(db, POSTS_COLL, postId, "replies", replyId);
  await updateDoc(ref, {
    text: clean,
    editedAt: serverTimestamp(),
    hasSpoiler: computedHasSpoiler,
  });
}

export async function softDeleteReply({ postId, replyId }) {
  const ref = doc(db, POSTS_COLL, postId, "replies", replyId);
  await updateDoc(ref, { deleted: true, status: "deleted" });

  await runTransaction(db, async (tx) => {
    const metaRef = doc(db, POSTS_COLL, postId);
    const snap = await tx.get(metaRef);               // leitura
    if (!snap.exists()) return;
    const cur = snap.data();
    const next = Math.max(0, (cur.repliesCount || 0) - 1);
    tx.update(metaRef, { repliesCount: next });       // escrita
  });
}

export async function setReplyStatus({ postId, replyId, status }) {
  if (!["published", "pending", "rejected", "deleted"].includes(status)) {
    throw new Error("Status inválido");
  }
  const ref = doc(db, POSTS_COLL, postId, "replies", replyId);

  // se publicar, também remove a bandeira de deleted
  const updatePayload = status === "published"
    ? { status, deleted: false }
    : { status };

  await updateDoc(ref, updatePayload);
}

// =============================
// Feed (leitura para UI)
// =============================
export async function loadInitialFeed() {
  const chunk = await getCurrentChunk();
  if (!chunk) return { chunk: null, posts: [] };
  const posts = (chunk.posts || []).filter(
    (p) => p.status !== "deleted" && p.status !== "rejected"
  );
  return { chunk, posts };
}

export async function loadMoreFrom(prevChunk) {
  if (!prevChunk) return { chunk: null, posts: [] };
  const older = await getPrevChunk(prevChunk.index);
  if (!older) return { chunk: null, posts: [] };
  const posts = (older.posts || []).filter(
    (p) => p.status !== "deleted" && p.status !== "rejected"
  );
  return { chunk: older, posts };
}

// =============================
// Helpers de UI
// =============================
export function canShowKebab({ currentUid, authorUid }) {
  return !!currentUid && currentUid === authorUid;
}

export function normalizeDate(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === "function") return ts.toDate();
  return new Date(ts);
}
